/**
 * Operator SPEC REVIEW (M2) — the resolved spec + provenance + option list + review tasks for one
 * vehicle. The operator can OVERRIDE any field (wins all decode sources), trigger the paid-API decode
 * (~$9/VIN), and see per-field confidence + the exact spec_fingerprint the comps oracle keys on.
 */
import type { SessionUser, Transmission, TransmissionConfidence, UpdateVehicleSpecInput } from '@motoveo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { Layout } from '../components/Layout';
import { dollarsToCents, formatCents, formatDate } from '../lib/format';

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="field">
      <span className="field-label">{label}</span>
      <span className="field-value">{value ?? '—'}</span>
    </div>
  );
}

function confidencePill(c: string) {
  return <span className={`pill conf-${c}`}>{c}</span>;
}

interface FormState {
  model_designation: string;
  exterior_color: string;
  exterior_color_code: string;
  paint_to_sample: boolean;
  pts_color_name: string;
  interior_color: string;
  interior_material: string;
  transmission: Transmission;
  transmission_confidence: TransmissionConfidence;
  base_msrp: string;
  options_msrp: string;
  total_msrp: string;
}

export function SpecReviewPage({ user }: { user: SessionUser }) {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState | null>(null);
  const [paidMsg, setPaidMsg] = useState<string | null>(null);

  const review = useQuery({ queryKey: ['spec', id], queryFn: () => api.vehicles.spec(id), enabled: Boolean(id) });

  // seed the override form when the spec loads / changes.
  useEffect(() => {
    const s = review.data?.spec;
    if (s && !form) {
      setForm({
        model_designation: s.model_designation ?? '',
        exterior_color: s.exterior_color ?? '',
        exterior_color_code: s.exterior_color_code ?? '',
        paint_to_sample: s.paint_to_sample,
        pts_color_name: s.pts_color_name ?? '',
        interior_color: s.interior_color ?? '',
        interior_material: s.interior_material ?? '',
        transmission: s.transmission,
        transmission_confidence: s.transmission_confidence,
        base_msrp: s.base_msrp_cents != null ? String(s.base_msrp_cents / 100) : '',
        options_msrp: s.options_msrp_cents != null ? String(s.options_msrp_cents / 100) : '',
        total_msrp: s.total_msrp_cents != null ? String(s.total_msrp_cents / 100) : '',
      });
    }
  }, [review.data, form]);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['spec', id] });
    setForm(null);
  };

  const save = useMutation({
    mutationFn: (patch: UpdateVehicleSpecInput) => api.vehicles.updateSpec(id, patch),
    onSuccess: invalidate,
  });

  const paid = useMutation({
    mutationFn: () => api.vehicles.decodePaid(id),
    onSuccess: async (r) => {
      setPaidMsg(
        r.paid_api_status === 'not_configured'
          ? 'Paid API not configured (no key in this environment) — set MOTOVEO_STICKER_API_KEY to enable.'
          : `Paid decode ${r.paid_api_status} (cost ${formatCents(r.cost_cents ?? 0)}).`,
      );
      await invalidate();
    },
    onError: (e) => setPaidMsg((e as Error).message),
  });

  const submitOverride = () => {
    if (!form) return;
    const patch: UpdateVehicleSpecInput = {
      model_designation: form.model_designation || null,
      exterior_color: form.exterior_color || null,
      exterior_color_code: form.exterior_color_code || null,
      paint_to_sample: form.paint_to_sample,
      pts_color_name: form.pts_color_name || null,
      interior_color: form.interior_color || null,
      interior_material: form.interior_material || null,
      transmission: form.transmission,
      transmission_confidence: form.transmission_confidence,
      base_msrp_cents: dollarsToCents(form.base_msrp),
      options_msrp_cents: dollarsToCents(form.options_msrp),
      total_msrp_cents: dollarsToCents(form.total_msrp),
    };
    save.mutate(patch);
  };

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => (f ? { ...f, [k]: v } : f));

  return (
    <Layout user={user}>
      {review.isLoading ? (
        <p className="muted">Loading spec…</p>
      ) : review.isError ? (
        <p className="error">{(review.error as Error).message}</p>
      ) : review.data ? (
        (() => {
          const { vehicle: v, spec, options, sources, review_tasks: tasks } = review.data;
          return (
            <>
              <div className="row-between">
                <h2>
                  {spec?.model_designation ?? `${v.model_year ?? ''} ${v.make ?? ''} ${v.model ?? ''}`.trim()}{' '}
                  <span className="pill">{v.decode_status}</span> {spec ? confidencePill(spec.confidence) : null}
                  {spec?.verified_by_operator ? <span className="pill conf-high">operator-verified</span> : null}
                </h2>
                <div className="inline">
                  <button onClick={() => paid.mutate()} disabled={paid.isPending}>
                    {paid.isPending ? 'Decoding…' : 'Decode via paid API (~$9)'}
                  </button>
                </div>
              </div>
              <p className="muted">
                VIN <code>{v.vin}</code> · generation {v.series_generation ?? '—'} · primary source{' '}
                <strong>{v.primary_spec_source ?? '—'}</strong>
              </p>
              {paidMsg ? <p className="warn">{paidMsg}</p> : null}
              {spec?.spec_fingerprint ? (
                <p className="muted">
                  spec_fingerprint <code>{spec.spec_fingerprint}</code>
                </p>
              ) : null}

              {tasks.filter((t) => t.status === 'open' || t.status === 'in_progress').length > 0 ? (
                <div className="card warn-card">
                  <h3>Needs operator review</h3>
                  {tasks
                    .filter((t) => t.status === 'open' || t.status === 'in_progress')
                    .map((t) => (
                      <div key={t.id} className="field">
                        <span className="field-label">
                          {t.kind} <span className="pill">{t.priority}</span>
                        </span>
                        <span className="field-value">
                          {Array.isArray((t.detail as { reasons?: string[] }).reasons)
                            ? (t.detail as { reasons: string[] }).reasons.join('; ')
                            : (t.detail as { codes?: string[] }).codes?.join(', ') ?? '—'}
                        </span>
                      </div>
                    ))}
                </div>
              ) : null}

              <section className="grid-2">
                <div className="card">
                  <h3>Resolved spec &amp; override</h3>
                  {form ? (
                    <div className="form-grid">
                      <label>
                        Model designation
                        <input value={form.model_designation} onChange={(e) => set('model_designation', e.target.value)} />
                      </label>
                      <label>
                        Transmission
                        <select value={form.transmission} onChange={(e) => set('transmission', e.target.value as Transmission)}>
                          {(['manual', 'pdk', 'unknown'] as const).map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Transmission confidence
                        <select
                          value={form.transmission_confidence}
                          onChange={(e) => set('transmission_confidence', e.target.value as TransmissionConfidence)}
                        >
                          {(['confirmed', 'inferred', 'unknown'] as const).map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="checkbox">
                        <input type="checkbox" checked={form.paint_to_sample} onChange={(e) => set('paint_to_sample', e.target.checked)} />
                        Paint to Sample
                      </label>
                      <label>
                        Exterior colour
                        <input value={form.exterior_color} onChange={(e) => set('exterior_color', e.target.value)} />
                      </label>
                      <label>
                        Exterior colour code
                        <input value={form.exterior_color_code} onChange={(e) => set('exterior_color_code', e.target.value)} />
                      </label>
                      <label>
                        PTS colour name
                        <input value={form.pts_color_name} onChange={(e) => set('pts_color_name', e.target.value)} />
                      </label>
                      <label>
                        Interior colour
                        <input value={form.interior_color} onChange={(e) => set('interior_color', e.target.value)} />
                      </label>
                      <label>
                        Interior material
                        <input value={form.interior_material} onChange={(e) => set('interior_material', e.target.value)} />
                      </label>
                      <label>
                        Base MSRP ($)
                        <input value={form.base_msrp} onChange={(e) => set('base_msrp', e.target.value)} />
                      </label>
                      <label>
                        Options MSRP ($)
                        <input value={form.options_msrp} onChange={(e) => set('options_msrp', e.target.value)} />
                      </label>
                      <label>
                        Total MSRP ($)
                        <input value={form.total_msrp} onChange={(e) => set('total_msrp', e.target.value)} />
                      </label>
                      <div className="row-between">
                        <button onClick={submitOverride} disabled={save.isPending}>
                          {save.isPending ? 'Saving…' : 'Save override (verify spec)'}
                        </button>
                        <button className="secondary" onClick={() => setForm(null)} disabled={save.isPending}>
                          Reset
                        </button>
                      </div>
                      {save.isError ? <p className="error">{(save.error as Error).message}</p> : null}
                    </div>
                  ) : (
                    <p className="muted">No spec resolved yet — decode the VIN first.</p>
                  )}
                </div>

                <div className="card">
                  <h3>Provenance</h3>
                  <Field label="Spec source" value={spec?.spec_source} />
                  <Field label="Confidence" value={spec ? confidencePill(spec.confidence) : '—'} />
                  <Field label="Total MSRP" value={formatCents(spec?.total_msrp_cents ?? null)} />
                  <h4>Decode sources</h4>
                  {sources.length === 0 ? (
                    <p className="muted">none yet</p>
                  ) : (
                    <ul className="tight">
                      {sources.map((s) => (
                        <li key={s.id}>
                          <strong>{s.source_type}</strong> · {s.provider ?? '—'} · {s.status} ·{' '}
                          {s.cost_cents > 0 ? formatCents(s.cost_cents) : 'free'} · {formatDate(s.created_at)}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>

              <div className="card">
                <h3>Options ({options.length})</h3>
                {options.length === 0 ? (
                  <p className="muted">No option codes parsed yet — upload the window sticker or run the paid decode.</p>
                ) : (
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Code</th>
                          <th>Description</th>
                          <th>Category</th>
                          <th>MSRP</th>
                          <th>Source</th>
                          <th>Flags</th>
                        </tr>
                      </thead>
                      <tbody>
                        {options.map((o) => (
                          <tr key={o.id}>
                            <td>
                              <code>{o.normalized_code}</code>
                            </td>
                            <td>{o.description_snapshot ?? <span className="muted">unmapped</span>}</td>
                            <td className="muted">{o.category ?? '—'}</td>
                            <td>{formatCents(o.option_msrp_cents)}</td>
                            <td className="muted">{o.source}</td>
                            <td>
                              {o.is_high_value ? <span className="pill conf-high">high-value</span> : null}
                              {o.option_dictionary_id ? null : <span className="pill">unmapped</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          );
        })()
      ) : null}
    </Layout>
  );
}
